import { ReactNode } from "react";
import { ArrowUp, ArrowDown, ArrowUpDown } from "lucide-react";
import { TableHead } from "@/components/ui/table";
import {
  Tooltip,
  TooltipContent,
  TooltipProvider,
  TooltipTrigger,
} from "@/components/ui/tooltip";
import { cn } from "@/lib/utils";

export type SortDirection = "asc" | "desc";

export function getNextSort<T extends string>(
  currentField: T | null,
  currentDirection: SortDirection,
  clicked: T,
): { field: T | null; direction: SortDirection } {
  if (currentField !== clicked) return { field: clicked, direction: "desc" };
  if (currentDirection === "desc") return { field: clicked, direction: "asc" };
  return { field: null, direction: "desc" };
}

interface SortableTableHeadProps<TField extends string> {
  children: ReactNode;
  field: TField;
  activeField: TField | null;
  direction: SortDirection;
  onSort: (field: TField) => void;
  tooltip?: ReactNode;
  className?: string;
  tooltipClassName?: string;
  sticky?: boolean;
}

export function SortableTableHead<TField extends string>({
  children,
  field,
  activeField,
  direction,
  onSort,
  tooltip,
  className,
  tooltipClassName,
  sticky,
}: SortableTableHeadProps<TField>) {
  const isActive = activeField === field;
  const Icon = isActive ? (direction === "asc" ? ArrowUp : ArrowDown) : ArrowUpDown;

  const labelNode = tooltip ? (
    <TooltipProvider delayDuration={150}>
      <Tooltip>
        <TooltipTrigger
          asChild
          onClick={(e) => {
            e.stopPropagation();
            onSort(field);
          }}
        >
          <span className="cursor-pointer font-bold border-b border-dotted border-muted-foreground">
            {children}
          </span>
        </TooltipTrigger>
        <TooltipContent className={cn("max-w-sm", tooltipClassName)}>
          {tooltip}
        </TooltipContent>
      </Tooltip>
    </TooltipProvider>
  ) : (
    <span>{children}</span>
  );

  return (
    <TableHead
      className={cn("cursor-pointer select-none", className)}
      onClick={() => onSort(field)}
      sticky={sticky}
    >
      <span
        className={cn(
          "inline-flex items-center gap-1.5",
          className?.includes("text-right") && "justify-end",
          className?.includes("text-center") && "justify-center",
        )}
      >
        {labelNode}
        <Icon
          className={cn(
            "h-3 w-3 shrink-0 transition-colors hover:text-primary",
            isActive ? "text-primary" : "opacity-40 hover:opacity-100",
          )}
        />
      </span>
    </TableHead>
  );
}
